import { apiClient } from "../client";
import type { Envelope, LaravelPaginator } from "./catalog";

/**
 * Booking + Razorpay-for-bookings endpoints (verified against the Postman
 * collection's "Bookings" and "Payments — Razorpay" folders). All of these
 * are Sanctum-guarded, so they ride the BFF session cookie like the rest of
 * the app — no manual bearer token handling needed here.
 */

/** Verified against the live response — no "available" flag, since the
 * endpoint only returns free slots. */
export interface AvailableSlot {
  id?: string | number;
  start_time: string;
  end_time?: string;
  /** Not present on the live payload today, but tolerate it if added later. */
  available?: boolean;
  [key: string]: unknown;
}

/**
 * Verified against the live response: { status, duration, rate, slots }.
 * The astrologer's default session duration/rate ride along for convenience,
 * but the slot list itself is under `slots`, not `data`.
 */
export interface AvailableSlotsResponse {
  status?: boolean;
  duration?: number;
  rate?: string;
  slots: AvailableSlot[];
  [key: string]: unknown;
}

/** `standard` = a regular consultation (any astrologer, incl. Raju Maharaj's
 * tiered one); `gemstone` = Raju Maharaj's gemstone consultation. */
export type BookingCategory = "standard" | "gemstone";

/**
 * Raju Maharaj's gemstone consultation offer (GET /gemstone-consultation).
 * Bookable every day between start_time and end_time; `rate` is the price of
 * ONE whole session in rupees and is decided by the backend, never the client.
 */
export interface GemstoneConsultation {
  is_enabled: boolean;
  rate: number;
  /** Minutes per session (15/30/45/60). */
  duration: number;
  /** HH:mm */
  start_time: string;
  /** HH:mm */
  end_time: string;
  consultation_types: Array<"video" | "phone" | "inperson">;
  astrologer: { id: number; name: string; display_name: string | null; image_url: string | null } | null;
  /** Only on the astrologer-dashboard read. */
  updated_at?: string;
}

/** GET /gemstone-consultation/slots?date= — empty `slots` when switched off. */
export interface GemstoneSlotsResponse {
  status?: boolean;
  is_enabled: boolean;
  astrologer_id: number | null;
  date: string;
  duration: number;
  rate: number;
  /** ids look like `gemstone_0_<unix>` — pass verbatim as slot_id. */
  slots: AvailableSlot[];
}

/** The dashboard endpoints use the `{ success, message, data }` envelope. */
export interface GemstoneDashboardResponse {
  success: boolean;
  message?: string;
  data: GemstoneConsultation;
}

/** Partial update — send only what changes. */
export interface GemstoneSettingsPayload {
  is_enabled?: boolean;
  rate?: number;
  duration?: number;
  start_time?: string;
  end_time?: string;
  consultation_types?: Array<"video" | "phone" | "inperson">;
}

export interface BookingRecord {
  id: number;
  category?: BookingCategory;
  astrologer_id?: number | string;
  astrologer?: { id?: number; name?: string; display_name?: string } | null;
  date?: string | null;
  time?: string | null;
  duration?: number | string | null;
  consultation_type?: string | null;
  status?: string | null;
  [key: string]: unknown;
}

/** Astrologer record as nested on the booking-by-id payload (verified against
 * the live response) — carries pivot-loaded skills/languages for the detail
 * pages. Sensitive KYC/bank fields also ride along upstream; the UI must not
 * render them. */
export interface BookingAstrologer {
  id: number;
  user_id?: number;
  name?: string | null;
  /** Linked user record — carries the astrologer's real first/last name
   * (selected columns only; preferred over display_name in the UI). */
  user?: { id: number; first_name?: string | null; last_name?: string | null } | null;
  first_name?: string | null;
  last_name?: string | null;
  display_name?: string | null;
  slug?: string | null;
  short_intro?: string | null;
  consultation_mode?: string | null;
  experience?: number | string | null;
  rate?: string | null;
  duration?: number | null;
  online_status?: string | null;
  is_active?: boolean;
  skills?: Array<{ id: number; name: string; [key: string]: unknown }>;
  languages?: Array<{ id: number; name: string; [key: string]: unknown }>;
  [key: string]: unknown;
}

/**
 * Full booking detail from GET /booking-by-id/:id (verified against the live
 * response). Superset of the list-shaped BookingRecord: adds seeker contact +
 * birth details, payment ids, astrologer-note state and the nested astrologer.
 */
export interface BookingDetail extends Omit<BookingRecord, "astrologer"> {
  user_id?: number;
  slot_id?: number | null;
  name?: string | null;
  phone?: string | null;
  email?: string | null;
  birth_date?: string | null;
  birth_time?: string | null;
  place?: string | null;
  /** "2026-07-20 10:30:00" */
  scheduled_at?: string | null;
  type?: string | null;
  rate?: string | null;
  meeting_id?: string | null;
  /** Which video service this consultation's room runs on — stamped server-side
   * the first time either party opens the call (null before). The call page
   * never decides; it reads the join payload (see `bookings.joinCall`). */
  video_provider?: "videosdk" | "livekit" | string | null;
  meeting_started_at?: string | null;
  notes?: string | null;
  payment_method?: string | null;
  promo_code?: string | null;
  astrologer_note?: string | null;
  astrologer_note_status?: string | null;
  final_confirmation_from_astrologer?: boolean;
  razorpay_order_id?: string | null;
  razorpay_payment_id?: string | null;
  astrologer?: BookingAstrologer | null;
}

/**
 * Canonical path for the single-booking resource. GET today; the same path is
 * slated to grow other verbs (PATCH/POST sub-actions) — build every future
 * call from this helper so they all stay on one route.
 */
export const bookingByIdPath = (id: number | string) => `/booking-by-id/${id}`;

/**
 * GET /bookings/:id/call/join — the 1-1 consultation's room credentials for
 * the caller, on whichever video provider the booking runs on. Mirrors the
 * live-class JoinLiveClassPayload so one normaliser (`resolveVideoSession`)
 * serves both: `token` is the SDK's own JWT (VideoSDK auth token / LiveKit
 * access token), `meeting_id` the VideoSDK meetingId / LiveKit room name.
 * Role is server-signed (the booking's astrologer → "astrologer"/host, its
 * customer → "seeker"); the server also locks the booking to the admin's
 * current provider the first time either side opens the call.
 */
export interface JoinConsultationPayload {
  provider?: "videosdk" | "livekit" | string;
  meeting_id?: string;
  token?: string;
  user_name?: string | null;
  role?: "astrologer" | "seeker" | string;
  is_host?: boolean;
  can_publish?: boolean;
  /** The other party's user id — for the consultation:* socket signalling. */
  counterpart_user_id?: number | null;
  /** Phone bookings: both ends keep the camera off. */
  audio_only?: boolean;
  duration_min?: number;
  meeting_started_at?: string | null;
  /** ISO timestamp — when `token` stops working. */
  expires_at?: string;
  booking?: Partial<BookingDetail> & { video_provider?: string | null };
  /** VideoSDK only: the participantId pinned inside `token`. */
  participant_id?: string;
  /** LiveKit only: the wss:// URL to connect to. */
  server_url?: string;
  /** LiveKit only: the room name (same value as meeting_id). */
  room_name?: string;
  /** LiveKit only: this participant's identity as pinned inside `token`. */
  identity?: string;
  [key: string]: unknown;
}

export interface JoinConsultationResponse {
  success?: boolean;
  message?: string;
  data?: JoinConsultationPayload;
}

/** POST /bookings/:id/call/end — astrologer only; closes the room at the
 * provider for everyone still in it. Does NOT complete the booking (Finalize
 * does). */
export interface EndConsultationResponse {
  success?: boolean;
  message?: string;
  data?: { id: number; video_provider?: string | null; meeting_id?: string | null; room_closed?: boolean };
}

/** POST /bookings/:id/call/ring — astrologer/admin only. `rang` is false when
 * a ring within the last 10 s was reused instead. */
export interface RingConsultationResponse {
  success?: boolean;
  message?: string;
  data?: { id: number; call_id: string; call_invited_at: string; status: string; rang: boolean };
}

/** Verified against the legacy portal's AstrologerApiService: the upstream
 * reschedule takes the chosen slot's id, not a raw time. */
export interface RescheduleBookingPayload {
  booking_id: number | string;
  /** YYYY-MM-DD */
  date: string;
  /** `id` of the chosen slot from GET /available-slots. */
  slot_id: number | string;
}

/** "YYYY-MM-DD HH:mm:ss" (UTC) — the shape Laravel's toDateTimeString()
 * produces; the legacy portal treats these timestamps as UTC. */
const nowDateTimeString = () => new Date().toISOString().slice(0, 19).replace("T", " ");

export interface CreateBookingPayload {
  /** Omit (or "standard") for a regular booking. "gemstone" books Raju
   * Maharaj's gemstone consultation: astrologer_id becomes optional, `rate`
   * is ignored (the backend sets the price) and slot_id must come from
   * `gemstoneSlots`. */
  category?: BookingCategory;
  name: string;
  phone: string;
  email: string;
  consultation_type: string;
  /** Required for a standard booking; optional for a gemstone one. */
  astrologer_id?: number | string;
  /** YYYY-MM-DD */
  date: string;
  /** `id` of the chosen slot from GET /available-slots. */
  slot_id: number | string;
  payment_method?: string;
  /** Optional passthrough — overrides the astrologer's own registered rate
   * as the booking's stored price. Needed whenever the charged amount
   * differs from the astrologer's base rate (e.g. tiered/priority pricing). */
  rate?: number | string;
  /** YYYY-MM-DD */
  birth_date?: string;
  /** HH:mm */
  birth_time?: string;
  place?: string;
  notes?: string;
  [key: string]: unknown;
}

export interface RazorpayOrderPayload {
  /** Amount in rupees (not paise) — the backend converts to paise itself
   * when creating the Razorpay order. Verified live: sending paise here
   * caused a 100x overcharge. */
  amount: number;
  currency?: string;
  /** Required by the backend — the booking must exist (via bookings.create)
   * before an order can be created for it. */
  booking_id: number | string;
}

/**
 * Razorpay bootstrap data. Like every other endpoint in this API, the
 * payload rides under `data` — callers must unwrap it (`response.data ??
 * response`) before reading `id`/`amount`/etc., the same pattern used for
 * bookings/astrologers elsewhere in this file.
 */
export interface RazorpayOrderResponse {
  status?: boolean;
  message?: string;
  data?: {
    /** Razorpay's own order id (e.g. "order_xxx") — the SDK's native field name. */
    id?: string;
    order_id?: string;
    razorpay_order_id?: string;
    amount?: number | string;
    currency?: string;
    /** Razorpay public key id, when the backend echoes it back. */
    key?: string;
    [key: string]: unknown;
  };
  /** Some deployments may echo these at the top level too — kept as a fallback. */
  id?: string;
  order_id?: string;
  razorpay_order_id?: string;
  amount?: number | string;
  currency?: string;
  key?: string;
  [key: string]: unknown;
}

export interface RazorpayVerifyPayload {
  booking_id: number | string;
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
  [key: string]: unknown;
}

export interface RazorpayVerifyResponse {
  status: boolean;
  message?: string;
  booking?: BookingRecord;
  [key: string]: unknown;
}

export interface CreateBookingAfterPaymentPayload {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  astrologer_id: number | string;
  /** YYYY-MM-DD */
  date: string;
  /** HH:mm, 24-hour */
  time: string;
  duration?: number;
  consultation_type?: string;
  [key: string]: unknown;
}

export const bookings = {
  /**
   * Free/busy slots for one astrologer on one day. Accepts an AbortSignal so
   * callers (React Query) can cancel an in-flight request when the date
   * changes again before the previous one resolves — without this, rapid
   * date changes (or React 19 StrictMode's dev-only double-invoke) leave
   * stale requests racing the latest one and can make the slot list appear
   * to flicker or go blank.
   */
  availableSlots: (
    params: { astrologer_id: number | string; date: string },
    opts: { signal?: AbortSignal } = {}
  ) =>
    apiClient.get<AvailableSlotsResponse | AvailableSlot[]>("/available-slots", {
      params: { ...params },
      signal: opts.signal,
    }),

  /**
   * Reserve a slot (unpaid — pair with the Razorpay order/verify below).
   * Accepts FormData for the Maharaj flow's "unknown birth time" case, which
   * rides the seeker's photo + both palm photos alongside the usual fields —
   * the Content-Type override lets the browser attach the multipart boundary
   * itself instead of the client's default `application/json`.
   */
  create: (payload: CreateBookingPayload | FormData) =>
    apiClient.post<Envelope<BookingRecord> | BookingRecord>("/bookings", payload, {
      headers: payload instanceof FormData ? { "Content-Type": "multipart/form-data" } : undefined,
    }),

  /** Raju Maharaj's gemstone consultation: is it on, price, length, types. Public. */
  gemstoneOffer: () =>
    apiClient.get<Envelope<GemstoneConsultation>>("/gemstone-consultation"),

  /** Free gemstone consultation slots for one day (every day is bookable). Public. */
  gemstoneSlots: (params: { date: string }, opts: { signal?: AbortSignal } = {}) =>
    apiClient.get<GemstoneSlotsResponse>("/gemstone-consultation/slots", {
      params: { ...params },
      signal: opts.signal,
    }),

  /** Raju Maharaj's dashboard: read the gemstone consultation settings (403 for anyone else). */
  gemstoneSettings: () =>
    apiClient.get<GemstoneDashboardResponse>("/astrologer/gemstone-consultation"),

  /** Raju Maharaj's dashboard: partial update of the gemstone consultation settings. */
  updateGemstoneSettings: (payload: GemstoneSettingsPayload) =>
    apiClient.put<GemstoneDashboardResponse>("/astrologer/gemstone-consultation", payload),

  /** The signed-in user's bookings. `category` narrows to one kind. */
  list: (params: { page?: number; status?: string; category?: BookingCategory } = {}) =>
    apiClient.get<
      Envelope<LaravelPaginator<BookingRecord> | BookingRecord[]> | BookingRecord[]
    >("/bookings", { params: { ...params } }),

  get: (id: number | string) =>
    apiClient.get<Envelope<BookingRecord> | BookingRecord>(`/bookings/${id}`),

  /**
   * One booking with everything the detail pages need (seeker + birth facts,
   * payment ids, note state, nested astrologer). Works for both sides of the
   * consultation — the seeker's booking page and the astrologer's appointment
   * page render from this same read.
   */
  byId: (id: number | string) =>
    apiClient.get<Envelope<BookingDetail>>(bookingByIdPath(id)),

  /** Room credentials for the 1-1 call, on whichever provider the booking is
   * locked to (see JoinConsultationPayload). Astrologer, seeker or admin only. */
  joinCall: (id: number | string) =>
    apiClient.get<JoinConsultationResponse>(`/bookings/${id}/call/join`),

  /** Astrologer: close the call room for everyone at the provider. */
  endCall: (id: number | string) =>
    apiClient.post<EndConsultationResponse>(`/bookings/${id}/call/end`, {}),

  /** Astrologer: ring the seeker's phone - call on EVERY Start / re-ring, even
   * when the booking already reads live. Each ring is a new call_id (the app's
   * Decline silences one call, not the booking); the server throttles repeats
   * within 10 s and moves confirmed -> ready_to_start itself. */
  ringCall: (id: number | string) =>
    apiClient.post<RingConsultationResponse>(`/bookings/${id}/call/ring`, {}),

  /** PATCH the same booking-by-id resource — partial updates (e.g. { status:
   * "completed" }). All payload shapes below are verified against the legacy
   * portal's AstrologerApiService, the upstream's only existing consumer. */
  updateById: (id: number | string, payload: Record<string, unknown>) =>
    apiClient.patch<Envelope<BookingDetail>>(bookingByIdPath(id), payload),

  /** Astrologer opens the consultation: exact legacy payload — the meeting id
   * `astro-{id}` rides along with the ready_to_start transition. */
  startVideo: (id: number | string) =>
    apiClient.patch<Envelope<BookingDetail>>(bookingByIdPath(id), {
      meeting_id: `astro-${id}`,
      status: "ready_to_start",
    }),

  /** Seeker enters the room: marks the session live and stamps the moment the
   * countdown starts from (both sides derive remaining time from it). */
  joinVideo: (id: number | string) =>
    apiClient.patch<Envelope<BookingDetail>>(bookingByIdPath(id), {
      meeting_id: `astro-${id}`,
      meeting_started_at: nowDateTimeString(),
      status: "in_progress",
    }),

  /** Session over (host action or timer expiry). */
  endVideo: (id: number | string) =>
    apiClient.patch<Envelope<BookingDetail>>(bookingByIdPath(id), {
      meeting_ended_at: nowDateTimeString(),
      status: "completed",
    }),

  /** Cancel — the upstream cancel IS a status PATCH on booking-by-id (the
   * legacy portal never uses a dedicated cancel route). */
  cancelViaStatus: (id: number | string) =>
    apiClient.patch<Envelope<BookingDetail>>(bookingByIdPath(id), { status: "cancelled" }),

  /** Move a booking to a new slot (POST /booking/reschedule). */
  reschedule: (payload: RescheduleBookingPayload) =>
    apiClient.post<Envelope<BookingRecord> | BookingRecord>("/booking/reschedule", payload),

  /** Astrologer-only: save/replace the consultation note draft. */
  updateAstrologerNote: (id: number | string, note: string) =>
    apiClient.patch<Envelope<BookingDetail>>(`/bookings/${id}/astrologer-note`, {
      astrologer_note: note,
    }),

  /** Astrologer-only: finalize the note — locks it and publishes it on the
   * seeker's booking page. Exact legacy payload (both flags together). */
  finalizeAstrologerNote: (id: number | string) =>
    apiClient.patch<Envelope<BookingDetail>>(`/bookings/${id}/astrologer-note-status`, {
      astrologer_note_status: "finalized",
      final_confirmation_from_astrologer: true,
    }),

  cancel: (id: number | string, reason?: string) =>
    apiClient.post<Envelope<BookingRecord> | BookingRecord>(`/bookings/${id}/cancel`, {
      reason,
    }),

  /** Create the Razorpay order for a just-created booking. */
  createRazorpayOrder: (payload: RazorpayOrderPayload) =>
    apiClient.post<RazorpayOrderResponse>("/razorpay/order", payload),

  /** Confirm a completed Razorpay payment (signature verification). */
  verifyRazorpayPayment: (payload: RazorpayVerifyPayload) =>
    apiClient.post<RazorpayVerifyResponse>("/razorpay/verify", payload),

  /**
   * Documented in the Postman collection as an alternate/legacy path.
   * NOT used by the app's primary flow: live testing confirmed
   * POST /razorpay/order requires an existing `booking_id`, so bookings must
   * be created via `create()` first, then paid — see BookingPage/
   * MaharajBookingPage. Kept here in case a reconciliation/retry flow needs it.
   */
  createBookingAfterPayment: (payload: CreateBookingAfterPaymentPayload) =>
    apiClient.post<Envelope<BookingRecord> | BookingRecord>("/razorpay/booking", payload),
};
